(ns renolit-roofing-service.view.laboratory-report-summary
  (:require [malli.util :as mu]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [renolit-roofing-service.layout.components :refer [entity-section]]
            [renolit-roofing-service.layout.display-item :as layout.display-item]
            [renolit-roofing-service.layout.display-item.impl]
            [renolit-roofing-service.layout.icon :as layout.icon]
            [renolit-roofing-service.localization :refer [t]]
            [renolit-roofing-service.navigation :as nav]
            [renolit-roofing-service.service.service :as srv.service]
            [renolit-roofing-service.util.localization :as util.localization]
            [renolit-roofing-service.view :as view]
            [renolit-roofing-service.view.installer-service-information.zone :as view.zone]))

(def path-params-schema
  [:map
   [:id
    [:uuid]]])

(def ^:const route-config
  {:name [:laboratory-report-summary]
   :auth-req? true
   :parameters {:path path-params-schema}
   :controllers [{:parameters {:path (mu/keys path-params-schema)}
                  :start #(rf/dispatch [::view.on-enter %])
                  :stop #(rf/dispatch [::view.on-leave %])}]})

(rf/reg-event-fx
 ::view.on-enter
 (fn [_ [_ opts]]
   (let [service-id (get-in opts [:path :id])]
     {:fx [[:dispatch [::srv.service/fetch-service-by-id service-id
                       {:on-success-evt [::service-loaded]}]]]})))

(rf/reg-event-fx
 ::service-loaded
 (fn [{:keys [db]} [_ service]]
   (let [conclusions (or (:conclusions service) {})]
     {:db (assoc db :lab-report-summary/conclusions conclusions)
      :fx [[:dispatch [::view.zone/initialize-zones-map]]]})))

(rf/reg-event-fx
 ::view.on-leave
 (fn [{:keys [db]} _]
   {:db (-> db
            (dissoc :lab-report-summary/conclusions)
            (dissoc :zones :zones-bounds :default-map-viewport
                    :gmaps/read-only? :gmaps/bridge-registry))
    :fx [[:dispatch [::srv.service/clear-service]]]}))

(rf/reg-sub
 ::conclusions
 (fn [db _]
   (get db :lab-report-summary/conclusions {})))

(rf/reg-sub
 ::project
 :<- [::srv.service/current-service]
 (fn [service _]
   (:project service)))

(rf/reg-sub
 ::zone-name-index
 :<- [::srv.service/current-service]
 (fn [service _]
   (let [zones (or (:zones service) [])]
     (into {} (map (juxt :id :name)) zones))))

(rf/reg-sub
 ::review-index
 :<- [::srv.service/current-service]
 (fn [service _]
   (let [reviews (or (:reviews service) [])]
     (into {} (map (juxt :id identity)) reviews))))

(rf/reg-sub
 ::all-observations
 :<- [::srv.service/current-service]
 :<- [::zone-name-index]
 :<- [::review-index]
 (fn [[service zone-names review-index] _]
   (let [observations (or (:observations service) [])]
     (->> observations
          (remove :clear)
          (mapv (fn [obs]
                  (let [review (get review-index (:review-id obs))
                        zone-id (:zone-id review)
                        zone-name (get zone-names zone-id "")]
                    (assoc obs
                           :zone-name zone-name
                           :review-type (:type review)
                           :wp-type (:wp-type review)))))))))

(rf/reg-sub
 ::observations-by-wp
 :<- [::all-observations]
 (fn [observations _]
   (group-by (comp keyword :wp) observations)))

(rf/reg-sub
 ::visual-inspection-observations
 :<- [::observations-by-wp]
 (fn [by-wp _]
   (get by-wp :visual-inspection [])))

(rf/reg-sub
 ::non-destructive-observations
 :<- [::observations-by-wp]
 (fn [by-wp _]
   (get by-wp :non-destructive-testing [])))

(rf/reg-sub
 ::destructive-observations
 :<- [::observations-by-wp]
 (fn [by-wp _]
   (get by-wp :destructive-testing [])))

(rf/reg-sub
 ::comments-by-observation
 :<- [::conclusions]
 (fn [conclusions _]
   (let [comments (or (:comments conclusions) [])]
     (into {} (map (juxt :observation-id :comment)) comments))))

(defn- report-service-data
  [service]
  [entity-section
   {:title (t [:page.laboratory.report/service-data])}
   [:div.si-info
    [layout.display-item/main
     {:type :text
      :state service
      :path [:name]
      :label-transl-kw :domain.service/name}]
    [layout.display-item/main
     {:type :text
      :value (util.localization/dropdown-value :service-origin (:origin @service))
      :label-transl-kw :domain.service/origin}]
    [layout.display-item/main
     {:type :text
      :value (util.localization/dropdown-value :service-reason (:reason @service))
      :label-transl-kw :domain.service/reason}]
    [layout.display-item/main
     {:type :text
      :state service
      :path [:reason-text]
      :label-transl-kw :domain.service/reason-text}]
    [layout.display-item/main
     {:type :text
      :state service
      :path [:objective]
      :label-transl-kw :domain.service/objective}]
    [layout.display-item/main
     {:type :text
      :state service
      :path [:manager]
      :label-transl-kw :domain.service/responsible}]
    [layout.display-item/main
     {:type :text
      :state service
      :path [:authorized-diagnostic-contractor]
      :label-transl-kw :domain.service/contractor}]
    [layout.display-item/main
     {:type :text
      :state service
      :path [:inspector]
      :label-transl-kw :domain.service/inspector}]]])

(defn- report-lifecycle-data
  [project]
  [entity-section
   {:title (t [:page.laboratory.report/lifecycle])}
   [:div.si-info
    [layout.display-item/main
     {:type :text
      :state project
      :path [:building-type]
      :label-transl-kw :page.project.details.expected-product-life}]
    [layout.display-item/main
     {:type :text
      :state project
      :path [:building-type]
      :label-transl-kw :page.project.details.actual-product-life}]
    [layout.display-item/main
     {:type :text
      :state project
      :path [:building-type]
      :label-transl-kw :page.project.details.critical-window}]
    [layout.display-item/main
     {:type :text
      :state project
      :path [:building-type-text]
      :label-transl-kw :page.project.details.end-of-guarantee}]
    [layout.display-item/main
     {:type :text
      :state project
      :path [:product]
      :label-transl-kw :page.project.details.end-of-critical-window}]]])

(defn- obs-type-label
  [obs]
  (if (:incidence-level obs)
    (t [:page.laboratory.report/type-incidence])
    (t [:page.laboratory.report/type-observation])))

(defn- comments-section-table
  [title observations all-comments]
  (let [rows (->> observations
                  (filter #(seq (get all-comments (:id %))))
                  (mapv (fn [obs]
                          {:observation-id (:id obs)
                           :type (obs-type-label obs)
                           :comment (get all-comments (:id obs))})))]
    [:div.lr-report-comments__section
     [:h4.lr-report-comments__section-title title]
     [:table.lr-report-comments__table
      [:thead
       [:tr
        [:th (t [:page.laboratory.report/comment-type])]
        [:th (t [:page.laboratory.report/comment-action])]]]
      [:tbody
       (if (seq rows)
         (doall
          (for [row rows]
            ^{:key (:observation-id row)}
            [:tr
             [:td (:type row)]
             [:td (:comment row)]]))
         [:tr
          [:td {:colSpan 2}
           (t [:common/no-data])]])]]]))

(defn- report-comments-table
  []
  (let [comments-index (rf/subscribe [::comments-by-observation])
        vi-obs (rf/subscribe [::visual-inspection-observations])
        ndt-obs (rf/subscribe [::non-destructive-observations])
        dt-obs (rf/subscribe [::destructive-observations])]
    (fn []
      (let [all-comments @comments-index]
        [entity-section
         {:title (t [:page.laboratory.report/important-comments])}
         [:div.lr-report-comments
          [comments-section-table
           (t [:page.laboratory.report/visual-inspection])
           @vi-obs all-comments]
          [comments-section-table
           (t [:page.laboratory.report/non-destructive])
           @ndt-obs all-comments]
          [comments-section-table
           (t [:page.laboratory.report/destructive])
           @dt-obs all-comments]]]))))

(defn- report-conclusions
  []
  (let [conclusions (rf/subscribe [::conclusions])]
    (fn []
      (let [{:keys [visual-inspection non-destructive-testing
                    destructive-testing membrane-life final]} @conclusions
            wp-entries [[:visual-inspection visual-inspection]
                        [:non-destructive-testing non-destructive-testing]
                        [:destructive-testing destructive-testing]]]
        [entity-section
         {:title (t [:page.laboratory.report/conclusion])}
         [:div.lr-report-conclusions
          [:div.lr-report-conclusions__visit-programs
           [:h4 (t [:page.laboratory.report/conclusion-per-visit])]
           (doall
            (for [[wp-key wp-text] wp-entries
                  :when (seq wp-text)]
              (let [wp-label (t [(keyword (str "domain.choices.wp/"
                                               (name wp-key)))])]
                ^{:key wp-key}
                [:div.lr-report-conclusions__item
                 [:span.lr-report-conclusions__wp-label wp-label]
                 [:p wp-text]])))]
          (when membrane-life
            [:div.lr-report-conclusions__membrane
             [:span (t [:page.laboratory.report/membrane-years])]
             [:span.lr-report-conclusions__value
              (str membrane-life)]])
          (when (seq final)
            [:div.lr-report-conclusions__final
             [:h4 (t [:page.laboratory.report/final-conclusion])]
             [:p final]])]]))))

(defn- navigation-links
  [service-id]
  [:div.lr-nav-links
   [:a.lr-nav-links__link
    {:href (nav/href [:installer-service-information] {:id service-id})}
    (t [:page.laboratory.report/go-to-project])
    [layout.icon/icon {:key :right-arrow}]]
   [:a.lr-nav-links__link
    {:href (nav/href [:installer-service-information] {:id service-id})}
    (t [:page.laboratory.report/go-to-service])
    [layout.icon/icon {:key :right-arrow}]]])

(defn main-view
  [service-atom project-atom]
  [:<>
   [:div.lr-report__map-and-data
    [:div.lr-report__map
     [view.zone/zones-map-wrapper]]
    [:div.lr-report__data
     [report-service-data service-atom]]]
   [report-lifecycle-data project-atom]
   [report-comments-table]
   [report-conclusions]])

(defn- main
  []
  (let [service @(rf/subscribe [::srv.service/current-service])
        project @(rf/subscribe [::project])
        service-atom (r/atom nil)
        project-atom (r/atom nil)]
    (if service
      (do
        (reset! service-atom service)
        (reset! project-atom project)
        (let [service-id (:id service)
              service-name (:name service)]
          [:div.user_layout
           [:div.lab-report
            [:div.lr-report
             [:div.lr-header
              [:a.back-link {:href (nav/href [:laboratory-report] {:id service-id})}
               [layout.icon/icon {:key :back}]
               (t [:page.laboratory.report/back-to-edit])]
              [:div.lr-header__title-row
               [:h3 service-name]]]
             [navigation-links service-id]
             [main-view service-atom project-atom]]]]))
      [:div.user_layout
       [:div.lab-report
        [:div.loading (t [:common.loading])]]])))

(defmethod view/view-display (:name route-config)
  [_]
  [main])
